import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation as useLibMutation } from '@tanstack/react-query';

import type { MutationFunction } from '@tanstack/react-query';

import type { MutationOptions, TApiError } from './types';

import openToaster from '../Toastr/openToastr';
import { parseError } from './parseError';

export function useMutation<
	TData = any,
	TError = TData,
	TVariables = unknown,
	TContext extends { parsedError?: TApiError<TError> } | object = {
		parsedError?: TApiError<TError>;
	}
>(
	mutationFn: MutationFunction<TData, TVariables>,
	options: MutationOptions<TData, TError, TVariables, TContext>
) {
	const [parsedError, setParsedError] = useState<TApiError<TError>>();

	const { operation, moduleName, disableToaster } = options;

	const { t } = useTranslation('common');
	const mutation = useLibMutation<TData, TError, TVariables, TContext>({
		...options,
		mutationFn,
		onMutate: (variables) => {
			setParsedError(undefined);
			return options?.onMutate?.(variables);
		},
		onSuccess: (...args) => {
			const variables = args[1];

			if (!disableToaster) {
				const successMessage =
					typeof options?.successMessage === 'function'
						? options?.successMessage(variables)
						: options?.successMessage;

				const op =
					typeof operation === 'function' ? operation(variables) : operation;

				openToaster({
					message: successMessage ?? t(`${op}_success`, { moduleName }),
				});
			}

			options.onSuccess?.(...args);
		},
		onError: async (error, variables, context) => {
			const parsedError = await parseError<TData>(error);

			if (!disableToaster) {
				const failMessage =
					typeof options?.failMessage === 'function'
						? options?.failMessage(variables)
						: options?.failMessage;

				const op =
					typeof operation === 'function' ? operation(variables) : operation;

				openToaster({
					message:
						failMessage ||
						parsedError?.message ||
						t(`${op}_fail`, { moduleName }),
					variant: 'error',
				});
			}

			options.onError?.(error, variables, { ...context!, parsedError });
		},
	});

	useEffect(() => {
		if (mutation?.error)
			parseError<TError>(mutation?.error)?.then(setParsedError);
	}, [mutation?.error]);

	return {
		...mutation,
		parsedError,
		isLoading: mutation.isPending,
	};
}
